import { useLocation, useMatches } from "@tanstack/react-router";
import { track } from "~/lib/analytics";
import { localizedContentPath } from "~/lib/localized-content-path";
import { $setLocale } from "~/lib/server/locale";
import * as m from "~/paraglide/messages.js";
import {
  getLocale,
  type Locale,
  locales,
  localizeUrl,
} from "~/paraglide/runtime.js";

const localeLabels: Record<Locale, string> = {
  en: "EN",
  lv: "LV",
};

interface LocalizedContent {
  localeSlugs?: Record<string, string> | null;
  slug?: string | null;
}

function getLocalizedContent(loaderData: unknown): LocalizedContent | null {
  if (!(loaderData && typeof loaderData === "object")) {
    return null;
  }

  if ("article" in loaderData) {
    return (
      (loaderData as { article?: LocalizedContent | null }).article ?? null
    );
  }

  if ("project" in loaderData) {
    return (
      (loaderData as { project?: LocalizedContent | null }).project ?? null
    );
  }

  return null;
}

export function LanguageSwitcher() {
  const currentLocale = getLocale();
  const pathname = useLocation({ select: (l) => l.pathname });
  const localizedContent = useMatches({
    select: (matches) => {
      for (let index = matches.length - 1; index >= 0; index -= 1) {
        const match = matches[index];
        if (!match) {
          continue;
        }
        const content = getLocalizedContent(match.loaderData);
        if (content) {
          return content;
        }
      }

      return null;
    },
  });

  return (
    // <fieldset> groups form controls; these are two navigation links.
    // biome-ignore lint/a11y/useSemanticElements: not a form control group
    <div
      aria-label={m.nav_language_label()}
      className="lang-switch"
      role="group"
    >
      {locales.map((locale) => {
        const isActive = locale === currentLocale;
        const targetPathname = localizedContentPath(
          pathname,
          locale,
          localizedContent
        );
        const targetUrl = localizeUrl(
          new URL(targetPathname, "https://lindevo.com"),
          { locale }
        );

        return (
          <a
            aria-current={isActive}
            href={targetUrl.pathname}
            key={locale}
            onClick={(event) => {
              event.preventDefault();
              if (locale !== currentLocale) {
                track("language_switch", {
                  from_locale: currentLocale,
                  to_locale: locale,
                });
              }
              $setLocale({ data: { locale } }).finally(() => {
                window.location.href = targetUrl.pathname;
              });
            }}
          >
            {localeLabels[locale]}
          </a>
        );
      })}
    </div>
  );
}
